import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL_WITH_API } from '../config/api';
import AdminHeader from '../components/AdminHeader';
import ImpersonationBanner from '../components/ImpersonationBanner';
import SideMenuCard, { SideMenuItem } from '../components/SideMenuCard';
import { getExtensionId } from '../config/extension';
import { isStagingOrLocalhost } from '../utils/environment';
import { GROUPS_FEATURE_ENABLED } from '../config/features';
import { clearAllAuthStorage, getAdminViewDomain, getAdminViewMode, getEffectiveToken, getEffectiveUser, getRealUser, isImpersonating, setAdminViewDomain } from '../utils/authSession';
import { syncPolicyPreviewDomain } from '../utils/policyPreview';
import { MENU_ICON_PATHS } from '../components/menuIcons';
import FeedbackFab from '../components/FeedbackFab';
import { exportPolicyToPdf, PolicyPdfColorBlock } from '../utils/policyPdf';

interface PolicyColor {
  title: string;
  description: string;
}

interface PolicySettings {
  domain?: string;
  allowTeacherOverride?: boolean;
  naOptionEnabled?: boolean;
  selectionReminderEnabled?: boolean;
  na?: PolicyColor;
  colorScale?: 2 | 3 | 4 | 5;
  red: PolicyColor;
  orange?: PolicyColor;
  yellow: PolicyColor;
  green: PolicyColor;
  blue?: PolicyColor;
  updatedAt?: string;
  updatedBy?: string;
}

type TemplateScale = 2 | 3 | 4 | 5;

interface SuperAdminPolicyResponse extends PolicySettings {
  activeTemplateScale?: TemplateScale;
  twoColorTemplate?: PolicySettings;
  threeColorTemplate?: PolicySettings;
  fourColorTemplate?: PolicySettings;
  fiveColorTemplate?: PolicySettings;
  fiveColorScaleEnabled?: boolean;
  groups?: Array<{ id: string; name: string }>;
  policyTarget?: { type: 'domain_template' | 'group'; groupId: string | null; name: string; hasOverride: boolean };
  naOptionAccessEnabled?: boolean;
  selectionReminderAccessEnabled?: boolean;
}

const DEFAULT_NA_COPY: PolicyColor = {
  title: 'N/A (Not Applicable)',
  description: 'The AI-usage policy does not apply to this post.'
};

const SCALE_HELP = 'Choose how many AI-usage levels are included.';
const OVERRIDE_HELP = 'When "Yes," teachers in this Policy Group can edit their own policies. When "No," they use this policy as written.';
const NA_OPTION_HELP = 'When "Yes," teachers will see an additional grey "N/A" option.';
const REMINDER_HELP = 'If "Yes," teachers will see a pop-up reminder if they forget to select a Traffic Lite color.';

const POLICY_META = {
  red: { label: 'Red policy', accent: '#ef4444', tint: 'rgba(239,68,68,0.04)' },
  orange: { label: 'Orange policy', accent: '#f59e0b', tint: 'rgba(245,158,11,0.08)' },
  yellow: { label: 'Yellow policy', accent: '#facc15', tint: 'rgba(254,249,195,0.72)' },
  green: { label: 'Green policy', accent: '#22c55e', tint: 'rgba(34,197,94,0.04)' },
  blue: { label: 'Blue policy', accent: '#2563eb', tint: 'rgba(37,99,235,0.14)' }
} as const;

const FOUR_COLOR_DEFAULTS: Required<Pick<PolicySettings, 'red' | 'orange' | 'yellow' | 'green'>> = {
  red: {
    title: 'AI-Free (Prohibited)',
    description: 'Assignments or tasks must be completed entirely without AI tools. Users rely solely on their own knowledge, skills, and effort.'
  },
  orange: {
    title: 'AI-Assisted (Brainstorming/Structure)',
    description: 'AI is permitted for ideation, brainstorming, outlining, or structuring work, but no AI-generated content may appear in the final submission. Disclosure of tools used is required.'
  },
  yellow: {
    title: 'AI-Enhanced (Editing/Refining)',
    description: 'AI is permitted to improve the quality of human-created work, such as grammar checking, rephrasing, or summarizing. No new substantive content is created by AI. Disclosure is required.'
  },
  green: {
    title: 'AI-Empowered (Full Integration)',
    description: 'AI is used to generate core content, analyze data, or complete significant parts of the task. The user acts as editor and evaluator, ensuring accuracy and mitigating bias. Full transparency and citation of AI tools are required.'
  }
};

const FIVE_COLOR_DEFAULTS: Required<Pick<PolicySettings, 'red' | 'orange' | 'yellow' | 'green' | 'blue'>> = {
  red: {
    title: 'No AI',
    description: 'The assessment is completed entirely without AI assistance in a controlled environment, ensuring that students rely solely on their existing knowledge, understanding, and skills.'
  },
  orange: {
    title: 'AI Planning',
    description: 'AI may be used for pre-task activities such as brainstorming, outlining, and initial research. This level focuses on the effective use of AI for planning, synthesis, and ideation, but assessments should emphasize the ability to develop and refine these ideas independently.'
  },
  yellow: {
    title: 'AI Collaboration',
    description: 'AI may be used to help complete the task, including idea generation, drafting, feedback, and refinement. Students should critically evaluate and modify the AI suggested outputs, demonstrating their understanding.'
  },
  green: {
    title: 'Full AI',
    description: 'AI may be used to complete any elements of the task, with students directing AI to achieve the assessment goals. Assessments at this level may also require engagement with AI to achieve goals and solve problems.'
  },
  blue: {
    title: 'AI Exploration',
    description: 'AI is used creatively to enhance problem-solving, generate novel insights, or develop innovative solutions to solve problems. Students and educators co-design assessments to explore unique AI applications within the field of study.'
  }
};

const THREE_COLOR_DEFAULTS: Pick<PolicySettings, 'red' | 'yellow' | 'green'> = {
  red: FIVE_COLOR_DEFAULTS.red,
  yellow: FIVE_COLOR_DEFAULTS.yellow,
  green: FIVE_COLOR_DEFAULTS.green
};

const normalizeScale = (rawScale?: number, fiveColorEnabled = false): TemplateScale => {
  if (rawScale === 5 && fiveColorEnabled) return 5;
  if (rawScale === 4) return 4;
  if (rawScale === 2) return 2;
  return 3;
};

const normalizePolicySettings = (
  raw: Partial<PolicySettings>,
  preferredScale?: TemplateScale,
  fiveColorEnabled = false
): PolicySettings => {
  const scale = normalizeScale(preferredScale ?? raw?.colorScale, fiveColorEnabled);
  return {
    ...raw,
    colorScale: scale,
    red: raw?.red || THREE_COLOR_DEFAULTS.red,
    yellow: raw?.yellow || THREE_COLOR_DEFAULTS.yellow,
    green: raw?.green || THREE_COLOR_DEFAULTS.green,
    orange: scale >= 4 ? (raw?.orange || FIVE_COLOR_DEFAULTS.orange) : undefined,
    blue: scale === 5 ? (raw?.blue || FIVE_COLOR_DEFAULTS.blue) : undefined,
    allowTeacherOverride: Boolean(raw?.allowTeacherOverride),
    naOptionEnabled: Boolean(raw?.naOptionEnabled),
    selectionReminderEnabled: Boolean(raw?.selectionReminderEnabled),
    na: {
      title: String(raw?.na?.title || '').trim() || DEFAULT_NA_COPY.title,
      description: String(raw?.na?.description || '').trim() || DEFAULT_NA_COPY.description
    }
  };
};

const deriveTemplates = (raw: Partial<SuperAdminPolicyResponse>, fiveColorEnabled: boolean) => {
  const twoColorTemplate = normalizePolicySettings(
    raw?.twoColorTemplate || raw,
    2,
    fiveColorEnabled
  );
  const threeColorTemplate = normalizePolicySettings(
    raw?.threeColorTemplate || raw,
    3,
    fiveColorEnabled
  );
  const fourColorTemplate = normalizePolicySettings(
    raw?.fourColorTemplate || raw,
    4,
    fiveColorEnabled
  );
  const fiveColorTemplate = normalizePolicySettings(
    raw?.fiveColorTemplate || raw,
    5,
    fiveColorEnabled
  );
  return { twoColorTemplate, threeColorTemplate, fourColorTemplate, fiveColorTemplate };
};

const SettingHelp = ({ text }: { text: string }) => {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <button
        type="button"
        aria-label={text}
        title={text}
        onClick={() => setOpen((value) => !value)}
        style={{
          width: '18px',
          height: '18px',
          borderRadius: '50%',
          border: '1px solid #9ca3af',
          background: '#fff',
          color: '#374151',
          fontSize: '12px',
          fontWeight: 700,
          cursor: 'pointer',
          lineHeight: '16px',
          padding: 0
        }}
      >
        ?
      </button>
      {open && (
        <span
          className="setting-help-popup"
          style={{
            position: 'absolute',
            top: '24px',
            left: 0,
            zIndex: 20,
            width: '260px',
            background: '#111827',
            color: '#fff',
            fontSize: '12px',
            lineHeight: 1.45,
            padding: '8px 10px',
            borderRadius: '8px',
            fontWeight: 500
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
};

const signedInDomain = () => {
  const email = String(getRealUser()?.email || '').trim().toLowerCase();
  const at = email.lastIndexOf('@');
  return at >= 0 ? email.slice(at + 1) : '';
};

const readApiError = async (response: Response, fallback: string) => {
  const data = await response.json().catch(() => ({} as { error?: string }));
  return data?.error || fallback;
};

const SettingsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [menuOpen, setMenuOpen] = useState(false);
  const usePersistentSidebar = isStagingOrLocalhost();
  const [settings, setSettings] = useState<PolicySettings>({
    allowTeacherOverride: false,
    colorScale: 4,
    red: FOUR_COLOR_DEFAULTS.red,
    orange: FOUR_COLOR_DEFAULTS.orange,
    yellow: FOUR_COLOR_DEFAULTS.yellow,
    green: FOUR_COLOR_DEFAULTS.green
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewDomains, setPreviewDomains] = useState<string[]>([]);
  const [previewDomain, setPreviewDomain] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSuperAdminUser, setIsSuperAdminUser] = useState(false);
  const [selectedTemplateTab, setSelectedTemplateTab] = useState<TemplateScale>(3);
  const [fiveColorScaleEnabled, setFiveColorScaleEnabled] = useState(false);
  const [originalSettings, setOriginalSettings] = useState<PolicySettings | null>(null);
  const [naOptionAccessEnabled, setNaOptionAccessEnabled] = useState(false);
  const [selectionReminderAccessEnabled, setSelectionReminderAccessEnabled] = useState(false);
  const [scaleTemplates, setScaleTemplates] = useState<{
    twoColorTemplate: PolicySettings;
    threeColorTemplate: PolicySettings;
    fourColorTemplate: PolicySettings;
    fiveColorTemplate: PolicySettings;
  } | null>(null);
  const [groups, setGroups] = useState<Array<{ id: string; name: string }>>([]);
  const [groupUserCounts, setGroupUserCounts] = useState<Record<string, number>>({});
  const [selectedPolicyTarget, setSelectedPolicyTarget] = useState<string>('domain_template');
  const [policyTargetMenuOpen, setPolicyTargetMenuOpen] = useState(false);
  const [policyActionsOpen, setPolicyActionsOpen] = useState(false);
  const [copyMenuOpen, setCopyMenuOpen] = useState(false);
  const [copyTargetGroupIds, setCopyTargetGroupIds] = useState<string[]>([]);
  const [copyingPolicy, setCopyingPolicy] = useState(false);
  const [resettingPolicy, setResettingPolicy] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [pdfTitle, setPdfTitle] = useState('');
  const [pdfSubtitle, setPdfSubtitle] = useState('');
  const [exportingPdf, setExportingPdf] = useState(false);
  const policyTargetMenuRef = React.useRef<HTMLDivElement | null>(null);
  const policyActionsRef = React.useRef<HTMLDivElement | null>(null);
  const scopedDomainFromQuery = String(searchParams.get('domain') || '').trim().toLowerCase();
  const scopedGroupIdFromQuery = String(searchParams.get('groupId') || '').trim();
  const superAdminDomainView = isSuperAdminUser && Boolean(scopedDomainFromQuery);
  const superAdminTemplateMode = isSuperAdminUser && !superAdminDomainView;
  const impersonating = isImpersonating();

  useEffect(() => {
    document.title = scopedDomainFromQuery
      ? `Policy Details - ${scopedDomainFromQuery} - Traffic Lite Admin`
      : 'Settings - Traffic Lite Admin';
    let viewerIsSuperAdmin = false;
    try {
      const user = getEffectiveUser();
      if (user) {
        viewerIsSuperAdmin = Boolean(user?.isSuperAdmin) && !impersonating;
        setIsSuperAdminUser(viewerIsSuperAdmin);
      }
    } catch (parseError) {
      console.warn('Could not parse trafficLiteUser from localStorage:', parseError);
    }
    const initialPolicyTarget = !viewerIsSuperAdmin && GROUPS_FEATURE_ENABLED && scopedGroupIdFromQuery
      ? scopedGroupIdFromQuery
      : 'domain_template';
    setSelectedPolicyTarget(initialPolicyTarget);
    if (getAdminViewMode() === 'domain' && signedInDomain() === 'gmail.com') {
      void loadSchoolPreview();
      return;
    }
    fetchSettings(viewerIsSuperAdmin, initialPolicyTarget);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impersonating, scopedDomainFromQuery, scopedGroupIdFromQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (policyActionsRef.current && !policyActionsRef.current.contains(event.target as Node)) {
        setPolicyActionsOpen(false);
        setCopyMenuOpen(false);
      }
      if (policyTargetMenuRef.current && !policyTargetMenuRef.current.contains(event.target as Node)) {
        setPolicyTargetMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = () => {
    if (!window.confirm('Are you sure you want to sign out?')) {
      return;
    }

    clearAllAuthStorage();

    const chromeApi = (window as any).chrome;
    if (chromeApi?.runtime?.sendMessage) {
      const message = { action: 'CLEAR_ALL_DATA' };
      const extensionId = getExtensionId();
      try {
        if (extensionId) {
          chromeApi.runtime.sendMessage(extensionId, message, () => {});
        } else {
          chromeApi.runtime.sendMessage(message, () => {});
        }
      } catch (error) {
        console.error('Sign-out storage clear failed:', error);
      }
    }

    window.location.href = `${window.location.origin}${window.location.pathname}#/signout-confirmation`;
  };

  const loadSchoolPreview = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getEffectiveToken();
      if (!token) {
        setError('Not authenticated. Please sign in again.');
        setLoading(false);
        return;
      }
      const response = await fetch(`${API_BASE_URL_WITH_API}/admin/domains`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Skip-Admin-View': '1'
        },
        cache: 'no-store'
      });
      if (!response.ok) {
        throw new Error(await readApiError(response, 'Failed to load domains'));
      }
      const data = await response.json();
      const domains = (Array.isArray(data?.domains) ? data.domains : [])
        .map((row: { domain?: string }) => String(row?.domain || '').trim().toLowerCase())
        .filter((domain: string) => domain && domain !== 'gmail.com');
      setPreviewDomains(domains);
      const stored = getAdminViewDomain();
      const nextDomain = domains.includes(stored) ? stored : (domains[0] || '');
      setPreviewDomain(nextDomain);
      if (!nextDomain) {
        setError('No school domain is available.');
        setLoading(false);
        return;
      }
      setAdminViewDomain(nextDomain);
      syncPolicyPreviewDomain(nextDomain);
      await fetchSettings(false, 'domain_template');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load domains');
      setLoading(false);
    }
  };

  const fetchSettings = async (viewerOverride?: boolean, targetOverride?: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const viewerIsSuperAdmin = typeof viewerOverride === 'boolean' ? viewerOverride : isSuperAdminUser;
      const token = getEffectiveToken();
      
      if (!token) {
        setError('Not authenticated. Please sign in again.');
        setLoading(false);
        return;
      }

      const activeTarget = targetOverride || selectedPolicyTarget;
      const params = new URLSearchParams();
      if (viewerIsSuperAdmin && scopedDomainFromQuery) {
        params.append('domain', scopedDomainFromQuery);
      } else if (!viewerIsSuperAdmin && GROUPS_FEATURE_ENABLED && activeTarget && activeTarget !== 'domain_template') {
        params.append('groupId', activeTarget);
      }
      const response = await fetch(`${API_BASE_URL_WITH_API}/admin/policy-settings${params.toString() ? `?${params.toString()}` : ''}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error(await readApiError(response, `Failed to fetch settings: ${response.statusText}`));
      }

      const data: SuperAdminPolicyResponse = await response.json();
      if (GROUPS_FEATURE_ENABLED && !viewerIsSuperAdmin) {
        const nextGroups = Array.isArray(data.groups) ? data.groups : [];
        setGroups(nextGroups);
        const resolvedTarget = data.policyTarget?.type === 'group' && data.policyTarget.groupId
          ? data.policyTarget.groupId
          : 'domain_template';
        setSelectedPolicyTarget(resolvedTarget);
      }
      const fiveColorEnabled = Boolean(data?.fiveColorScaleEnabled);
      setFiveColorScaleEnabled(fiveColorEnabled);
      const templates = deriveTemplates(data, fiveColorEnabled);
      setScaleTemplates(templates);
      const activeScale = viewerIsSuperAdmin && !scopedDomainFromQuery
        ? selectedTemplateTab
        : normalizeScale(data?.activeTemplateScale ?? data?.colorScale, fiveColorEnabled);
      const effectiveActiveScale = activeScale === 5 && !fiveColorEnabled ? 4 : activeScale;
      const activeSettings =
        effectiveActiveScale === 5
          ? templates.fiveColorTemplate
          : (effectiveActiveScale === 4 ? templates.fourColorTemplate : (effectiveActiveScale === 2 ? templates.twoColorTemplate : templates.threeColorTemplate));
      const hydratedActiveSettings = normalizePolicySettings({
        ...activeSettings,
        colorScale: effectiveActiveScale,
        allowTeacherOverride: viewerIsSuperAdmin && !scopedDomainFromQuery
          ? false
          : Boolean(data?.allowTeacherOverride),
        naOptionEnabled: viewerIsSuperAdmin && !scopedDomainFromQuery
          ? false
          : Boolean(data?.naOptionEnabled),
        selectionReminderEnabled: viewerIsSuperAdmin && !scopedDomainFromQuery
          ? false
          : Boolean(data?.selectionReminderEnabled),
        na: data?.na
      }, effectiveActiveScale, fiveColorEnabled);
      setNaOptionAccessEnabled(viewerIsSuperAdmin && !scopedDomainFromQuery
        ? false
        : Boolean(data?.naOptionAccessEnabled));
      setSelectionReminderAccessEnabled(viewerIsSuperAdmin && !scopedDomainFromQuery
        ? false
        : Boolean(data?.selectionReminderAccessEnabled));
      setSettings(hydratedActiveSettings);
      setOriginalSettings(hydratedActiveSettings);
      console.log('✅ Settings loaded:', data);
    } catch (err: any) {
      console.error('❌ Error fetching settings:', err);
      setError(err.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupUserCounts = async () => {
    if (isSuperAdminUser || !GROUPS_FEATURE_ENABLED) return;
    try {
      const token = getEffectiveToken();
      if (!token) return;
      const baseUrl = API_BASE_URL_WITH_API.endsWith('/api')
        ? API_BASE_URL_WITH_API
        : `${API_BASE_URL_WITH_API}/api`;
      const response = await fetch(`${baseUrl}/admin/users`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        cache: 'no-store'
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) return;
      const users = Array.isArray(data?.users) ? data.users : [];
      const counts: Record<string, number> = {};
      users.forEach((user: any) => {
        const isTeacher = (user?.rawRole || user?.role) === 'teacher' && !user?.isAdmin;
        if (!isTeacher || !user?.groupId) return;
        counts[String(user.groupId)] = (counts[String(user.groupId)] || 0) + 1;
      });
      setGroupUserCounts(counts);
    } catch (error) {
      console.warn('Could not load group user counts:', error);
    }
  };

  useEffect(() => {
    void fetchGroupUserCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdminUser, groups.length]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const token = getEffectiveToken();
      
      if (!token) {
        setError('Not authenticated. Please sign in again.');
        setSaving(false);
        return;
      }

      const payload = superAdminTemplateMode
        ? (() => {
            const templateScale = selectedTemplateTab;
            const activeTemplate =
              templateScale === 5
                ? (scaleTemplates?.fiveColorTemplate || settings)
                : (templateScale === 4
                  ? (scaleTemplates?.fourColorTemplate || settings)
                  : (templateScale === 2
                    ? (scaleTemplates?.twoColorTemplate || settings)
                    : (scaleTemplates?.threeColorTemplate || settings)));
            return {
              colorScale: templateScale,
              red: activeTemplate.red,
              ...(templateScale >= 4 && activeTemplate.orange ? { orange: activeTemplate.orange } : {}),
              ...(templateScale >= 3 && activeTemplate.yellow ? { yellow: activeTemplate.yellow } : {}),
              green: activeTemplate.green,
              ...(templateScale === 5 && activeTemplate.blue ? { blue: activeTemplate.blue } : {})
            };
          })()
        : {
            allowTeacherOverride: Boolean(settings.allowTeacherOverride),
            naOptionEnabled: Boolean(settings.naOptionEnabled),
            selectionReminderEnabled: Boolean(settings.selectionReminderEnabled),
            na: settings.na || DEFAULT_NA_COPY,
            colorScale: settings.colorScale === 5 ? 5 : (settings.colorScale === 4 ? 4 : (settings.colorScale === 2 ? 2 : 3)),
            red: settings.red,
            ...(settings.colorScale >= 4 && settings.orange ? { orange: settings.orange } : {}),
            ...(settings.colorScale >= 3 && settings.yellow ? { yellow: settings.yellow } : {}),
            green: settings.green,
            ...(settings.colorScale === 5 && settings.blue ? { blue: settings.blue } : {})
          };

      const params = new URLSearchParams();
      if (isSuperAdminUser && scopedDomainFromQuery) {
        params.append('domain', scopedDomainFromQuery);
      } else if (!isSuperAdminUser && GROUPS_FEATURE_ENABLED && selectedPolicyTarget !== 'domain_template') {
        params.append('groupId', selectedPolicyTarget);
      }
      const response = await fetch(`${API_BASE_URL_WITH_API}/admin/policy-settings${params.toString() ? `?${params.toString()}` : ''}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(await readApiError(response, `Failed to save: ${response.statusText}`));
      }

      const data: SuperAdminPolicyResponse = await response.json();
      console.log('✅ Settings saved:', data);
      setSuccessMessage(superAdminTemplateMode ? 'Policy template saved successfully!' : 'Policy settings saved successfully!');
      await fetchSettings(isSuperAdminUser);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('❌ Error saving settings:', err);
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const createGroup = async (name: string) => {
    const token = getEffectiveToken();
    if (!token) throw new Error('Not authenticated');
    const response = await fetch(`${API_BASE_URL_WITH_API}/admin/groups`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ name })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to create group');
    return data.group as { id: string; name: string };
  };

  const handleFieldChange = (
    color: 'red' | 'orange' | 'yellow' | 'green' | 'blue',
    field: 'title' | 'description',
    value: string
  ) => {
    setSettings((prev) => ({
      ...prev,
      [color]: {
        ...(prev[color] || { title: '', description: '' }),
        [field]: value
      }
    }));
    setScaleTemplates((prev) => {
      if (!prev) return prev;
      const activeScale = superAdminTemplateMode
        ? selectedTemplateTab
        : (settings.colorScale === 5 ? 5 : (settings.colorScale === 4 ? 4 : (settings.colorScale === 2 ? 2 : 3)));
      const key = activeScale === 5 ? 'fiveColorTemplate' : (activeScale === 4 ? 'fourColorTemplate' : (activeScale === 2 ? 'twoColorTemplate' : 'threeColorTemplate'));
      const currentTemplate = prev[key];
      return {
        ...prev,
        [key]: {
          ...currentTemplate,
          [color]: {
            ...(currentTemplate[color] || { title: '', description: '' }),
            [field]: value
          }
        }
      };
    });
  };

  const getComparableSettings = (value: PolicySettings | null) => {
    if (!value) return null;
    return {
      colorScale: value.colorScale === 5 ? 5 : (value.colorScale === 4 ? 4 : (value.colorScale === 2 ? 2 : 3)),
      allowTeacherOverride: Boolean(value.allowTeacherOverride),
      naOptionEnabled: Boolean(value.naOptionEnabled),
      selectionReminderEnabled: Boolean(value.selectionReminderEnabled),
      na: value.na,
      red: value.red,
      orange: (value.colorScale || 3) >= 4 ? value.orange : undefined,
      yellow: (value.colorScale || 3) >= 3 ? value.yellow : undefined,
      green: value.green,
      blue: value.colorScale === 5 ? value.blue : undefined
    };
  };

  const hasUnsavedChanges =
    JSON.stringify(getComparableSettings(settings)) !== JSON.stringify(getComparableSettings(originalSettings));
  const handleColorScaleChange = (scale: 2 | 3 | 4 | 5) => {
    if (scale === 5 && !fiveColorScaleEnabled) return;
    if (scaleTemplates) {
      const nextTemplate =
        scale === 5
          ? scaleTemplates.fiveColorTemplate
          : (scale === 4 ? scaleTemplates.fourColorTemplate : (scale === 2 ? scaleTemplates.twoColorTemplate : scaleTemplates.threeColorTemplate));
      setSettings(
        normalizePolicySettings(
          {
            ...nextTemplate,
            allowTeacherOverride: Boolean(settings.allowTeacherOverride),
            naOptionEnabled: Boolean(settings.naOptionEnabled),
            selectionReminderEnabled: Boolean(settings.selectionReminderEnabled),
            na: settings.na
          },
          scale,
          fiveColorScaleEnabled
        )
      );
      return;
    }
    setSettings((prev) => {
      if (scale === 2) {
        return normalizePolicySettings(
          {
            ...prev,
            colorScale: 2,
            red: THREE_COLOR_DEFAULTS.red,
            green: THREE_COLOR_DEFAULTS.green,
            orange: undefined,
            yellow: undefined,
            blue: undefined
          },
          2,
          fiveColorScaleEnabled
        );
      }
      if (scale === 4) {
        return normalizePolicySettings(
          {
            ...prev,
            colorScale: 4,
            red: FIVE_COLOR_DEFAULTS.red,
            orange: FIVE_COLOR_DEFAULTS.orange,
            yellow: FIVE_COLOR_DEFAULTS.yellow,
            green: FIVE_COLOR_DEFAULTS.green
          },
          4,
          fiveColorScaleEnabled
        );
      }
      if (scale === 5) {
        return normalizePolicySettings(
          {
            ...prev,
            colorScale: 5,
            red: FIVE_COLOR_DEFAULTS.red,
            orange: FIVE_COLOR_DEFAULTS.orange,
            yellow: FIVE_COLOR_DEFAULTS.yellow,
            green: FIVE_COLOR_DEFAULTS.green,
            blue: FIVE_COLOR_DEFAULTS.blue
          },
          5,
          fiveColorScaleEnabled
        );
      }
      return normalizePolicySettings(
        {
          ...prev,
          colorScale: 3,
          orange: undefined,
          blue: undefined
        },
        3,
        fiveColorScaleEnabled
      );
    });
  };

  const handleSuperAdminTabChange = (scale: TemplateScale) => {
    setSelectedTemplateTab(scale);
    if (!scaleTemplates) return;
    if (scale === 5 && !fiveColorScaleEnabled) return;
    const nextTemplate =
      scale === 5
        ? scaleTemplates.fiveColorTemplate
        : (scale === 4 ? scaleTemplates.fourColorTemplate : (scale === 2 ? scaleTemplates.twoColorTemplate : scaleTemplates.threeColorTemplate));
    const normalized = normalizePolicySettings(
      {
        ...nextTemplate,
        colorScale: scale,
        allowTeacherOverride: false
      },
      scale,
      fiveColorScaleEnabled
    );
    setSettings(normalized);
    setOriginalSettings(normalized);
  };

  const handlePolicyTargetChange = async (value: string) => {
    const nextValue = value || 'domain_template';
    setSuccessMessage(null);
    setSelectedPolicyTarget(nextValue);
    await fetchSettings(false, nextValue);
  };

  const buildCurrentPolicyPayload = () => ({
    colorScale: settings.colorScale === 5 ? 5 : (settings.colorScale === 4 ? 4 : (settings.colorScale === 2 ? 2 : 3)),
    red: settings.red,
    ...(settings.colorScale >= 4 && settings.orange ? { orange: settings.orange } : {}),
    ...(settings.colorScale >= 3 && settings.yellow ? { yellow: settings.yellow } : {}),
    green: settings.green,
    ...(settings.colorScale === 5 && settings.blue ? { blue: settings.blue } : {}),
    allowTeacherOverride: Boolean(settings.allowTeacherOverride),
    naOptionEnabled: Boolean(settings.naOptionEnabled),
    selectionReminderEnabled: Boolean(settings.selectionReminderEnabled),
    na: settings.na || DEFAULT_NA_COPY
  });

  const getActivePolicyColorsForPdf = (): PolicyPdfColorBlock[] => {
    const keys = settings.colorScale === 5
      ? (['red', 'orange', 'yellow', 'green', 'blue'] as const)
      : (settings.colorScale === 4
        ? (['red', 'orange', 'yellow', 'green'] as const)
        : (settings.colorScale === 2 ? (['red', 'green'] as const) : (['red', 'yellow', 'green'] as const)));
    return keys.map((key) => ({
      key,
      label: POLICY_META[key].label,
      accent: POLICY_META[key].accent,
      tint: POLICY_META[key].tint,
      title: settings[key]?.title || '',
      description: settings[key]?.description || ''
    }));
  };

  const handleCopyPolicyToGroups = async () => {
    if (copyTargetGroupIds.length === 0 || copyingPolicy) return;
    const token = getEffectiveToken();
    if (!token) {
      setError('Not authenticated. Please sign in again.');
      return;
    }
    setCopyingPolicy(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const payload = buildCurrentPolicyPayload();
      for (const groupId of copyTargetGroupIds) {
        const params = new URLSearchParams({ groupId });
        const response = await fetch(`${API_BASE_URL_WITH_API}/admin/policy-settings?${params.toString()}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || 'Failed to copy policy to selected groups.');
        }
      }
      setSuccessMessage(`Policy copied to ${copyTargetGroupIds.length} group${copyTargetGroupIds.length !== 1 ? 's' : ''}.`);
      setPolicyActionsOpen(false);
      setCopyMenuOpen(false);
      setCopyTargetGroupIds([]);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to copy policy.');
    } finally {
      setCopyingPolicy(false);
    }
  };

  const handleResetToDefaultPolicy = async () => {
    if (resettingPolicy) return;
    const confirmReset = window.confirm('Reset this policy to the Traffic Lite default template?');
    if (!confirmReset) return;
    const token = getEffectiveToken();
    if (!token) {
      setError('Not authenticated. Please sign in again.');
      return;
    }
    setResettingPolicy(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const scale = settings.colorScale === 5 ? 5 : (settings.colorScale === 4 ? 4 : (settings.colorScale === 2 ? 2 : 3));
      const templateResponse = await fetch(`${API_BASE_URL_WITH_API}/admin/policy-settings/default-template?scale=${scale}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const templateData = await templateResponse.json().catch(() => ({}));
      if (!templateResponse.ok) {
        throw new Error(templateData.error || 'Failed to fetch default template.');
      }

      const params = new URLSearchParams();
      if (GROUPS_FEATURE_ENABLED && selectedPolicyTarget !== 'domain_template') {
        params.append('groupId', selectedPolicyTarget);
      }
      const saveResponse = await fetch(`${API_BASE_URL_WITH_API}/admin/policy-settings${params.toString() ? `?${params.toString()}` : ''}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          colorScale: templateData.colorScale,
          red: templateData.red,
          ...(templateData.colorScale >= 4 && templateData.orange ? { orange: templateData.orange } : {}),
          ...(templateData.colorScale >= 3 && templateData.yellow ? { yellow: templateData.yellow } : {}),
          green: templateData.green,
          ...(templateData.colorScale === 5 && templateData.blue ? { blue: templateData.blue } : {}),
          allowTeacherOverride: false
        })
      });
      const saveData = await saveResponse.json().catch(() => ({}));
      if (!saveResponse.ok) {
        throw new Error(saveData.error || 'Failed to reset policy.');
      }
      setPolicyActionsOpen(false);
      setCopyMenuOpen(false);
      await fetchSettings(false, selectedPolicyTarget);
      setSuccessMessage('Policy reset to the Traffic Lite default template.');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to reset policy.');
    } finally {
      setResettingPolicy(false);
    }
  };

  const handleExportPolicyPdf = async () => {
    setExportingPdf(true);
    try {
      await exportPolicyToPdf({
        title: pdfTitle,
        subtitle: pdfSubtitle,
        colorBlocks: getActivePolicyColorsForPdf(),
        fileNamePrefix: 'admin-policy'
      });
      setExportModalOpen(false);
      setPolicyActionsOpen(false);
    } finally {
      setExportingPdf(false);
    }
  };

  const sidebarItems: SideMenuItem[] = [
    ...(isSuperAdminUser
      ? [
          { key: 'users', label: 'Users', onClick: () => navigate('/admin/users'), icon: MENU_ICON_PATHS.users },
          { key: 'domains', label: 'Domains', onClick: () => navigate('/admin/domains'), icon: MENU_ICON_PATHS.domains },
          { key: 'dashboard', label: 'Data Dashboard', onClick: () => navigate('/admin'), icon: MENU_ICON_PATHS.dataDashboard },
          { key: 'policy', label: superAdminDomainView ? 'Policy Details' : 'Policy Templates', onClick: () => navigate('/admin/settings'), isActive: true, icon: MENU_ICON_PATHS.policyDetails },
          { key: 'aiAssist', label: 'GPS', onClick: () => navigate('/admin/ai-assist'), icon: MENU_ICON_PATHS.aiAssist }
        ]
      : [
          { key: 'policy', label: 'Policy Details', onClick: () => navigate('/admin/settings'), isActive: true, icon: MENU_ICON_PATHS.policyDetails },
          { key: 'users', label: 'Users', onClick: () => navigate('/admin/users'), icon: MENU_ICON_PATHS.users },
          { key: 'groups', label: 'Groups', onClick: () => navigate('/admin/groups'), icon: MENU_ICON_PATHS.groups },
          { key: 'dashboard', label: 'Data Dashboard', onClick: () => navigate('/admin'), icon: MENU_ICON_PATHS.dataDashboard }
        ]),
    ...(isSuperAdminUser
      ? [
          { key: 'feedback', label: 'Feedback', onClick: () => navigate('/admin/feedback'), icon: MENU_ICON_PATHS.feedback },
          { key: 'signout', label: 'Sign Out', onClick: handleSignOut, isDanger: true, icon: MENU_ICON_PATHS.signOut }
        ]
      : [])
  ];

  const copyEligibleGroups = groups.filter((group) => group.id !== selectedPolicyTarget);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div
        className={usePersistentSidebar ? 'max-w-[1220px] mx-auto flex gap-6 items-start' : 'max-w-[980px] mx-auto'}
      >
        {usePersistentSidebar && <SideMenuCard title={isSuperAdminUser ? 'Super Admin Menu' : 'Admin Menu'} items={sidebarItems} />}
        <div
          className={`${usePersistentSidebar ? 'flex-1 min-w-0' : ''} bg-white rounded-2xl shadow-md p-4 sm:p-6 lg:p-8`}
          style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
        >
        <ImpersonationBanner />
        <AdminHeader
          title={isSuperAdminUser ? 'Traffic Lite Super Admin' : 'Traffic Lite Admin'}
          subtitle={isSuperAdminUser ? (superAdminDomainView ? 'Policy Details' : 'Policy Templates') : 'Policy Details'}
          helperText={
            isSuperAdminUser && !superAdminDomainView
              ? 'Configure default templates for first-time users and users who have not customized policy settings'
              : 'Configure the AI usage policy text that appears in Google Classroom assignments'
          }
          menuOpen={menuOpen}
          setMenuOpen={setMenuOpen}
        />

        {superAdminDomainView && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#eff6ff',
              border: '1px solid #93c5fd',
              borderRadius: '8px',
              color: '#1e3a8a',
              marginBottom: '16px',
              fontWeight: 700
            }}
          >
            Viewing Policy Details for {scopedDomainFromQuery}
          </div>
        )}

        {usePersistentSidebar && (
          <div style={{ marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {superAdminTemplateMode ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    backgroundColor: '#f9fafb'
                  }}
                >
                  <button
                    type="button"
                    onClick={() => handleSuperAdminTabChange(2)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      backgroundColor: selectedTemplateTab === 2 ? '#4285f4' : 'white',
                      color: selectedTemplateTab === 2 ? 'white' : '#111827',
                      fontWeight: 700,
                      fontSize: '14px'
                    }}
                  >
                    2-Color Template
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSuperAdminTabChange(3)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      backgroundColor: selectedTemplateTab === 3 ? '#4285f4' : 'white',
                      color: selectedTemplateTab === 3 ? 'white' : '#111827',
                      fontWeight: 700,
                      fontSize: '14px'
                    }}
                  >
                    3-Color Template
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSuperAdminTabChange(4)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      backgroundColor: selectedTemplateTab === 4 ? '#4285f4' : 'white',
                      color: selectedTemplateTab === 4 ? 'white' : '#111827',
                      fontWeight: 700,
                      fontSize: '14px'
                    }}
                  >
                    4-Color Template
                  </button>
                  {fiveColorScaleEnabled && (
                    <button
                      type="button"
                      onClick={() => handleSuperAdminTabChange(5)}
                      style={{
                        padding: '8px 14px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        backgroundColor: selectedTemplateTab === 5 ? '#4285f4' : 'white',
                        color: selectedTemplateTab === 5 ? 'white' : '#111827',
                        fontWeight: 700,
                        fontSize: '14px'
                      }}
                    >
                      5-Color Template
                    </button>
                  )}
                </div>
                <button
                  onClick={handleSave}
                  disabled={saving || !hasUnsavedChanges}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: saving || !hasUnsavedChanges ? '#9ca3af' : '#4285f4',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: 700,
                    cursor: saving || !hasUnsavedChanges ? 'not-allowed' : 'pointer'
                  }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            ) : (
              <>
                {GROUPS_FEATURE_ENABLED && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      gap: '16px',
                      flexWrap: 'wrap'
                    }}
                  >
                    <span style={{ fontSize: '17px', fontWeight: 800, color: '#374151' }}>Customize Policy For:</span>
                    <div ref={policyTargetMenuRef} style={{ position: 'relative', minWidth: '420px' }}>
                      <button
                        type="button"
                        onClick={() => setPolicyTargetMenuOpen((prev) => !prev)}
                        aria-label="Toggle policy target menu"
                        className="transition-colors hover:bg-gray-100"
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '10px',
                          padding: '8px 12px',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          fontSize: '15px',
                          fontWeight: 600,
                          color: '#111827',
                          backgroundColor: 'white',
                          textAlign: 'left',
                          cursor: 'pointer'
                        }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                          <span style={{ color: '#111827', fontSize: '15px', fontWeight: 600 }}>
                            {selectedPolicyTarget === 'domain_template'
                              ? 'Domain Template (Default Policy)'
                              : `${groups.find((group) => group.id === selectedPolicyTarget)?.name || 'Group'} (Group Policy)`}
                          </span>
                          {selectedPolicyTarget !== 'domain_template' && (
                            <span
                              style={{
                                color: '#111827',
                                fontSize: '13px',
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                                padding: 0
                              }}
                            >
                              {groupUserCounts[selectedPolicyTarget] || 0} users
                            </span>
                          )}
                        </span>
                        <span
                          style={{
                            color: '#6b7280',
                            padding: 0,
                            fontSize: '14px',
                            lineHeight: 1,
                            display: 'inline-flex'
                          }}
                        >
                          <img
                            src={policyTargetMenuOpen ? MENU_ICON_PATHS.upArrow : MENU_ICON_PATHS.downArrow}
                            alt=""
                            style={{ width: '11px', height: '11px', objectFit: 'contain' }}
                          />
                        </span>
                      </button>
                      {policyTargetMenuOpen && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 'calc(100% + 6px)',
                            left: 0,
                            width: '100%',
                            maxHeight: '280px',
                            overflowY: 'auto',
                            borderRadius: '8px',
                            border: '1px solid #d1d5db',
                            backgroundColor: 'white',
                            boxShadow: '0 8px 22px rgba(0,0,0,0.14)',
                            zIndex: 55,
                            padding: '6px'
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              void handlePolicyTargetChange('domain_template');
                              setPolicyTargetMenuOpen(false);
                            }}
                            className="traffic-menu-font w-full text-left px-3 py-2 rounded-md text-[14px] transition-colors hover:bg-gray-100"
                            style={{ color: '#1f2937', fontWeight: selectedPolicyTarget === 'domain_template' ? 700 : 500 }}
                          >
                            Domain Template (Default Policy)
                          </button>
                          {groups.map((group) => (
                            <button
                              key={group.id}
                              type="button"
                              onClick={() => {
                                void handlePolicyTargetChange(group.id);
                                setPolicyTargetMenuOpen(false);
                              }}
                              className="traffic-menu-font w-full text-left px-3 py-2 rounded-md transition-colors hover:bg-gray-100"
                              style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '10px',
                                border: 'none',
                                backgroundColor: selectedPolicyTarget === group.id ? '#f3f4f6' : undefined,
                                textAlign: 'left',
                                cursor: 'pointer'
                              }}
                            >
                              <span
                                className="traffic-menu-font text-[14px] transition-colors hover:text-blue-700"
                                style={{
                                  color: '#1f2937',
                                  fontWeight: selectedPolicyTarget === group.id ? 700 : 500
                                }}
                              >
                                {group.name} (Group Policy)
                              </span>
                              <span
                                style={{
                                  color: '#111827',
                                  fontSize: '13px',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {groupUserCounts[group.id] || 0} users
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div ref={policyActionsRef} style={{ position: 'relative' }}>
                      <button
                        type="button"
                        aria-label="Open policy actions menu"
                        onClick={() => setPolicyActionsOpen((prev) => !prev)}
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '9999px',
                          border: '1px solid #d1d5db',
                          backgroundColor: '#ffffff',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer'
                        }}
                      >
                        <img src={MENU_ICON_PATHS.actionsMenu} alt="" style={{ width: '18px', height: '18px' }} />
                      </button>
                      {policyActionsOpen && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 'calc(100% + 8px)',
                            right: 0,
                            minWidth: '240px',
                            borderRadius: '10px',
                            border: '1px solid #d1d5db',
                            backgroundColor: '#ffffff',
                            boxShadow: '0 8px 22px rgba(0,0,0,0.14)',
                            zIndex: 50,
                            padding: '8px'
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setPolicyActionsOpen(false);
                              setCopyMenuOpen(false);
                              if (selectedPolicyTarget && selectedPolicyTarget !== 'domain_template') {
                                const selectedGroupName = groups.find((group) => group.id === selectedPolicyTarget)?.name || '';
                                const params = new URLSearchParams();
                                params.set('groupId', selectedPolicyTarget);
                                if (selectedGroupName.trim()) {
                                  params.set('q', selectedGroupName.trim());
                                }
                                navigate(`/admin/users?${params.toString()}`);
                              } else {
                                const params = new URLSearchParams();
                                params.set('groupId', '__ungrouped__');
                                params.set('q', 'no group');
                                navigate(`/admin/users?${params.toString()}`);
                              }
                            }}
                            className="traffic-menu-font w-full text-left px-3 py-2 rounded-md text-[14px] transition-colors hover:bg-gray-100"
                            style={{ color: '#1f2937', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '10px' }}
                          >
                            <img src={MENU_ICON_PATHS.users} alt="" style={{ width: '16px', height: '16px' }} />
                            <span>View Users in this Group</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setPolicyActionsOpen(false);
                              navigate('/admin/groups');
                            }}
                            className="traffic-menu-font w-full text-left px-3 py-2 rounded-md text-[14px] transition-colors hover:bg-gray-100"
                            style={{ color: '#1f2937', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '10px' }}
                          >
                            <img src={MENU_ICON_PATHS.groups} alt="" style={{ width: '16px', height: '16px' }} />
                            <span>Manage Groups</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setCopyMenuOpen((prev) => !prev)}
                            className="traffic-menu-font w-full text-left px-3 py-2 rounded-md text-[14px] transition-colors hover:bg-gray-100"
                            style={{ color: '#1f2937', fontWeight: 500, display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between' }}
                          >
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                              <img src={MENU_ICON_PATHS.policyDetails} alt="" style={{ width: '16px', height: '16px' }} />
                              <span>Copy Policy to Group</span>
                            </span>
                            <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center' }}>
                              <img
                                src={copyMenuOpen ? MENU_ICON_PATHS.upArrow : MENU_ICON_PATHS.downArrow}
                                alt=""
                                style={{ width: '11px', height: '11px', objectFit: 'contain' }}
                              />
                            </span>
                          </button>
                          {copyMenuOpen && (
                            <div style={{ margin: '6px 4px', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#f9fafb' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <button
                                  type="button"
                                  onClick={() => setCopyTargetGroupIds(copyEligibleGroups.map((group) => group.id))}
                                  style={{ border: 'none', background: 'transparent', color: '#2563eb', textDecoration: 'underline', fontSize: '12px', cursor: 'pointer' }}
                                >
                                  Select all
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setCopyTargetGroupIds([])}
                                  style={{ border: 'none', background: 'transparent', color: '#2563eb', textDecoration: 'underline', fontSize: '12px', cursor: 'pointer' }}
                                >
                                  Clear
                                </button>
                              </div>
                              <div style={{ display: 'grid', gap: '6px', maxHeight: '180px', overflowY: 'auto', marginBottom: '8px' }}>
                                {copyEligibleGroups.map((group) => (
                                  <label key={group.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#1f2937' }}>
                                    <input
                                      type="checkbox"
                                      checked={copyTargetGroupIds.includes(group.id)}
                                      onChange={(e) => {
                                        setCopyTargetGroupIds((prev) => (
                                          e.target.checked
                                            ? Array.from(new Set([...prev, group.id]))
                                            : prev.filter((id) => id !== group.id)
                                        ));
                                      }}
                                    />
                                    <span>{group.name}</span>
                                  </label>
                                ))}
                                {copyEligibleGroups.length === 0 && (
                                  <div style={{ fontSize: '12px', color: '#6b7280' }}>No other groups available.</div>
                                )}
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setCopyMenuOpen(false);
                                    setCopyTargetGroupIds([]);
                                  }}
                                  style={{ border: '1px solid #d1d5db', backgroundColor: 'white', borderRadius: '6px', padding: '6px 9px', fontSize: '12px', cursor: 'pointer' }}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={() => void handleCopyPolicyToGroups()}
                                  disabled={copyTargetGroupIds.length === 0 || copyingPolicy}
                                  style={{ border: 'none', backgroundColor: copyTargetGroupIds.length === 0 || copyingPolicy ? '#9ca3af' : '#4285f4', color: 'white', borderRadius: '6px', padding: '6px 9px', fontSize: '12px', fontWeight: 700, cursor: copyTargetGroupIds.length === 0 || copyingPolicy ? 'not-allowed' : 'pointer' }}
                                >
                                  {copyingPolicy ? 'Saving...' : 'Save'}
                                </button>
                              </div>
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => void handleResetToDefaultPolicy()}
                            className="traffic-menu-font w-full text-left px-3 py-2 rounded-md text-[14px] transition-colors hover:bg-gray-100"
                            style={{ color: '#1f2937', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '10px' }}
                          >
                            <img src={MENU_ICON_PATHS.policyDetails} alt="" style={{ width: '16px', height: '16px' }} />
                            <span>{resettingPolicy ? 'Resetting...' : 'Reset to Default Policy'}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setExportModalOpen(true)}
                            className="traffic-menu-font w-full text-left px-3 py-2 rounded-md text-[14px] transition-colors hover:bg-gray-100"
                            style={{ color: '#1f2937', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '10px' }}
                          >
                            <img src={MENU_ICON_PATHS.export} alt="" style={{ width: '16px', height: '16px' }} />
                            <span>Export Policy to PDF</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '4px',
            color: '#c00',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#efe',
            border: '1px solid #8f8',
            borderRadius: '4px',
            color: '#080',
            marginBottom: '20px'
          }}>
            {successMessage}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#999'
          }}>
            Loading settings...
          </div>
        )}

        {/* Policy Editor */}
        {!loading && (
          <div style={{ marginBottom: '30px' }}>
            {previewDomains.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <span style={{ fontSize: '16px', fontWeight: 800, color: '#111827' }}>School domain</span>
                <select
                  aria-label="School domain"
                  value={previewDomain}
                  onChange={(event) => {
                    const nextDomain = event.target.value;
                    setPreviewDomain(nextDomain);
                    setAdminViewDomain(nextDomain);
                    syncPolicyPreviewDomain(nextDomain);
                    void fetchSettings(false, 'domain_template');
                  }}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    fontSize: '15px',
                    fontWeight: 600,
                    color: '#111827',
                    backgroundColor: 'white'
                  }}
                >
                  {previewDomains.map((domain) => (
                    <option key={domain} value={domain}>{domain}</option>
                  ))}
                </select>
              </div>
            )}
            {!isSuperAdminUser || superAdminDomainView ? (
              <div
                style={{
                  border: '1px solid #d1d5db',
                  borderRadius: '10px',
                  backgroundColor: '#f3f4f6',
                  padding: '18px',
                  marginLeft: '-8px',
                  marginRight: '-8px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <div style={{ fontSize: '16px', color: '#111827', fontWeight: 800 }}>Policy Settings</div>
                  <button
                    onClick={handleSave}
                    disabled={saving || !hasUnsavedChanges}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: saving || !hasUnsavedChanges ? '#9ca3af' : '#4285f4',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '15px',
                      fontWeight: 700,
                      cursor: saving || !hasUnsavedChanges ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  <span style={{ fontWeight: 700, color: '#111827', fontSize: '16px' }}>Scale:</span>
                  <SettingHelp text={SCALE_HELP} />
                  <select
                    value={String(settings.colorScale || 3)}
                    onChange={(e) => handleColorScaleChange(e.target.value === '5' ? 5 : (e.target.value === '4' ? 4 : (e.target.value === '2' ? 2 : 3)))}
                    style={{
                      padding: '9px 12px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#111827',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="2">2 colors</option>
                    <option value="3">3 colors</option>
                    <option value="4">4 colors</option>
                    {fiveColorScaleEnabled && <option value="5">5 colors</option>}
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 700, color: '#111827', fontSize: '15px' }}>Allow Teacher Override?</span>
                          <SettingHelp text={OVERRIDE_HELP} />
                          <select
                            value={Boolean(settings.allowTeacherOverride) ? 'yes' : 'no'}
                            onChange={(e) =>
                              setSettings((prev) => ({
                                ...prev,
                                allowTeacherOverride: e.target.value === 'yes'
                              }))
                            }
                            style={{
                              padding: '8px 12px',
                              border: '1px solid #cbd5e1',
                              borderRadius: '6px',
                              fontSize: '15px',
                              fontWeight: 600,
                              color: '#111827',
                              backgroundColor: 'white'
                            }}
                          >
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </select>
                        </div>
                        {naOptionAccessEnabled && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 700, color: '#111827', fontSize: '15px' }}>N/A Option?</span>
                            <SettingHelp text={NA_OPTION_HELP} />
                            <select
                              value={Boolean(settings.naOptionEnabled) ? 'yes' : 'no'}
                              onChange={(e) =>
                                setSettings((prev) => ({
                                  ...prev,
                                  naOptionEnabled: e.target.value === 'yes',
                                  na: prev.na || DEFAULT_NA_COPY
                                }))
                              }
                              style={{
                                padding: '8px 12px',
                                border: '1px solid #cbd5e1',
                                borderRadius: '6px',
                                fontSize: '15px',
                                fontWeight: 600,
                                color: '#111827',
                                backgroundColor: 'white'
                              }}
                            >
                              <option value="yes">Yes</option>
                              <option value="no">No</option>
                            </select>
                          </div>
                        )}
                        {selectionReminderAccessEnabled && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 700, color: '#111827', fontSize: '15px' }}>Selection Reminder?</span>
                            <SettingHelp text={REMINDER_HELP} />
                            <select
                              value={Boolean(settings.selectionReminderEnabled) ? 'yes' : 'no'}
                              onChange={(e) =>
                                setSettings((prev) => ({
                                  ...prev,
                                  selectionReminderEnabled: e.target.value === 'yes'
                                }))
                              }
                              style={{
                                padding: '8px 12px',
                                border: '1px solid #cbd5e1',
                                borderRadius: '6px',
                                fontSize: '15px',
                                fontWeight: 600,
                                color: '#111827',
                                backgroundColor: 'white'
                              }}
                            >
                              <option value="yes">Yes</option>
                              <option value="no">No</option>
                            </select>
                          </div>
                        )}
                      </div>
                </div>
                <div className="space-y-4">
                  {(settings.colorScale === 5
                    ? (['red', 'orange', 'yellow', 'green', 'blue'] as const)
                    : (settings.colorScale === 4
                      ? (['red', 'orange', 'yellow', 'green'] as const)
                      : (settings.colorScale === 2
                        ? (['red', 'green'] as const)
                        : (['red', 'yellow', 'green'] as const)))
                  ).map((color) => (
                    <div
                      key={color}
                      className="group cursor-pointer bg-white rounded-2xl shadow-md hover:shadow-lg focus-within:shadow-lg transition-all duration-200 border border-gray-200 hover:border-gray-300 focus-within:border-blue-200"
                      style={{
                        borderLeft: `12px solid ${POLICY_META[color].accent}`,
                        backgroundColor: POLICY_META[color].tint
                      }}
                    >
                      <div className="p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <span
                            style={{
                              width: '10px',
                              height: '10px',
                              borderRadius: '9999px',
                              backgroundColor: POLICY_META[color].accent
                            }}
                          />
                          <span className="text-sm font-semibold text-gray-600">{POLICY_META[color].label}</span>
                        </div>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-12 md:gap-6">
                          <div className="md:col-span-4">
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Title</div>
                            <textarea
                              value={settings[color]?.title || ''}
                              onChange={(e) => handleFieldChange(color, 'title', e.target.value)}
                              rows={3}
                              className="policy-editable-field w-full rounded-xl px-3 py-2 text-[15px] font-semibold leading-[1.35] text-gray-900 outline-none resize-none"
                              placeholder={`${color.charAt(0).toUpperCase() + color.slice(1)} policy title`}
                            />
                          </div>
                          <div className="md:col-span-8">
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Description</div>
                            <textarea
                              value={settings[color]?.description || ''}
                              onChange={(e) => handleFieldChange(color, 'description', e.target.value)}
                              rows={3}
                              className="policy-editable-field w-full rounded-xl px-3 py-2 text-[14px] leading-[1.55] text-gray-600 outline-none resize-y"
                              placeholder={`${color.charAt(0).toUpperCase() + color.slice(1)} policy description`}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {naOptionAccessEnabled && settings.naOptionEnabled && (
                    <div
                      className="bg-white rounded-2xl shadow-md border border-gray-200"
                      style={{
                        borderLeft: '12px solid #9ca3af',
                        backgroundColor: 'rgba(156,163,175,0.16)'
                      }}
                    >
                      <div className="p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <span
                            style={{
                              width: '10px',
                              height: '10px',
                              borderRadius: '9999px',
                              backgroundColor: '#9ca3af'
                            }}
                          />
                          <span className="text-sm font-semibold text-gray-600">N/A</span>
                        </div>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-12 md:gap-6">
                          <div className="md:col-span-4">
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Title</div>
                            <textarea
                              value={settings.na?.title || ''}
                              onChange={(e) =>
                                setSettings((prev) => ({
                                  ...prev,
                                  na: {
                                    title: e.target.value,
                                    description: prev.na?.description || DEFAULT_NA_COPY.description
                                  }
                                }))
                              }
                              rows={3}
                              className="policy-editable-field w-full rounded-xl px-3 py-2 text-[15px] font-semibold leading-[1.35] text-gray-900 outline-none resize-none"
                              placeholder="N/A (Not Applicable)"
                            />
                          </div>
                          <div className="md:col-span-8">
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Description</div>
                            <textarea
                              value={settings.na?.description || ''}
                              onChange={(e) =>
                                setSettings((prev) => ({
                                  ...prev,
                                  na: {
                                    title: prev.na?.title || DEFAULT_NA_COPY.title,
                                    description: e.target.value
                                  }
                                }))
                              }
                              rows={3}
                              className="policy-editable-field w-full rounded-xl px-3 py-2 text-[14px] leading-[1.55] text-gray-600 outline-none resize-y"
                              placeholder="The AI-usage policy does not apply to this post."
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-5">
              {(settings.colorScale === 5
                ? (['red', 'orange', 'yellow', 'green', 'blue'] as const)
                : (settings.colorScale === 4
                  ? (['red', 'orange', 'yellow', 'green'] as const)
                  : (settings.colorScale === 2
                    ? (['red', 'green'] as const)
                    : (['red', 'yellow', 'green'] as const)))
              ).map((color) => (
                <div
                  key={color}
                  className="group cursor-pointer bg-white rounded-2xl shadow-md hover:shadow-lg focus-within:shadow-lg transition-all duration-200 border border-gray-200 hover:border-gray-300 focus-within:border-blue-200"
                  style={{
                    borderLeft: `12px solid ${POLICY_META[color].accent}`,
                    backgroundColor: POLICY_META[color].tint
                  }}
                >
                  <div className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <span
                        style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '9999px',
                          backgroundColor: POLICY_META[color].accent
                        }}
                      />
                      <span className="text-sm font-semibold text-gray-600">{POLICY_META[color].label}</span>
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-12 md:gap-6">
                      <div className="md:col-span-4">
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Title</div>
                        <textarea
                          value={settings[color]?.title || ''}
                          onChange={(e) => handleFieldChange(color, 'title', e.target.value)}
                          rows={3}
                          className="policy-editable-field w-full rounded-xl px-3 py-2 text-[15px] font-semibold leading-[1.35] text-gray-900 outline-none resize-none"
                          placeholder={`${color.charAt(0).toUpperCase() + color.slice(1)} policy title`}
                        />
                      </div>
                      <div className="md:col-span-8">
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Description</div>
                        <textarea
                          value={settings[color]?.description || ''}
                          onChange={(e) => handleFieldChange(color, 'description', e.target.value)}
                          rows={3}
                          className="policy-editable-field w-full rounded-xl px-3 py-2 text-[14px] leading-[1.55] text-gray-600 outline-none resize-y"
                          placeholder={`${color.charAt(0).toUpperCase() + color.slice(1)} policy description`}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              </div>
            )}
          </div>
        )}
        {!isSuperAdminUser && <FeedbackFab />}
        {exportModalOpen && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(15,23,42,0.42)',
              zIndex: 1300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '16px'
            }}
          >
            <div style={{ width: '100%', maxWidth: '460px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #d1d5db', padding: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#111827' }}>Export Policy to PDF</h3>
              <p style={{ marginTop: '8px', marginBottom: '12px', fontSize: '13px', color: '#4b5563' }}>
                Optional title and subtitle will be shown above your policy content in the exported PDF.
              </p>
              <div style={{ display: 'grid', gap: '10px' }}>
                <input
                  value={pdfTitle}
                  onChange={(e) => setPdfTitle(e.target.value)}
                  placeholder="Title (optional)"
                  style={{ padding: '9px 11px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}
                />
                <input
                  value={pdfSubtitle}
                  onChange={(e) => setPdfSubtitle(e.target.value)}
                  placeholder="Subtitle (optional)"
                  style={{ padding: '9px 11px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}
                />
              </div>
              <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => setExportModalOpen(false)}
                  disabled={exportingPdf}
                  style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: 'white', fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handleExportPolicyPdf()}
                  disabled={exportingPdf}
                  style={{ padding: '8px 12px', borderRadius: '8px', border: 'none', backgroundColor: exportingPdf ? '#9ca3af' : '#4285f4', color: 'white', fontWeight: 700, cursor: exportingPdf ? 'not-allowed' : 'pointer' }}
                >
                  {exportingPdf ? 'Exporting...' : 'Export'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default SettingsPage;
